import type { HostedAppType } from '@bikecoach/api/app';
import { apiBaseUrl, unwrap } from '@cycling-coach/web/lib/api';
import { keys } from '@cycling-coach/web/lib/queries';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hc } from 'hono/client';

/** The hosted routes' typed client (`/api/hosted`, HOSTED-DECISIONS H2); same origin and cookie as the core's. */
export const hostedApi = hc<HostedAppType>(`${apiBaseUrl}/api/hosted`, { init: { credentials: 'include' } });

export const hostedKeys = { connections: ['hosted', 'connections'] as const };

export function useHostedConnections() {
  return useQuery({ queryKey: hostedKeys.connections, queryFn: async () => unwrap(await hostedApi.connections.$get()) });
}

/** After a Zwift login changes: the hosted view, the core's Zwift status, and the events page's "Get more". */
function useRefreshZwift() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: hostedKeys.connections }),
      qc.invalidateQueries({ queryKey: keys.zwift }),
      qc.invalidateQueries({ queryKey: keys.eventsStatus }),
    ]);
}

export function useSaveZwift() {
  const refresh = useRefreshZwift();
  return useMutation({
    mutationFn: async (login: { username: string; password: string }) => unwrap(await hostedApi.connections.zwift.$put({ json: login })),
    onSettled: refresh,
  });
}

export function useRemoveZwift() {
  const refresh = useRefreshZwift();
  return useMutation({
    mutationFn: async () => unwrap(await hostedApi.connections.zwift.$delete()),
    onSettled: refresh,
  });
}
